import type { CapturePlan } from '@shared/store/impl/audioSettings'

/**
 * Shared deep-linking contract for the Settings window. Settings is navigated
 * purely by URL — the main process loads the hash route when it opens the window
 * and re-navigates an already-open one via the `deeplink-received` event (see
 * window-deeplinks.tsx) — so a deep-link is just a route string plus query
 * params. Both processes build it from here so there's one source of truth.
 */

/** Tab value for the Meetings & Audio settings tab. */
export const MEETINGS_SETTINGS_TAB = 'meeting-notes'

/** Tab value for the Account settings tab. */
export const ACCOUNT_SETTINGS_TAB = 'account'

/** Tab value for the Integrations settings tab. */
export const INTEGRATIONS_SETTINGS_TAB = 'integrations'

/** Tab value for the Subscription settings tab. */
export const SUBSCRIPTION_SETTINGS_TAB = 'subscription'

/** Tab value for the Skills settings tab. */
export const SKILLS_SETTINGS_TAB = 'skills'

/**
 * Section id for the unified audio-capture permissions banner (microphone +
 * system audio) in the Meetings tab. Used both as the flash-highlight target and
 * as the React key/ref anchor for the banner.
 */
export const AUDIO_PERMISSIONS_SECTION = 'audio-permissions'

/**
 * Query param a settings tab reads to flash-highlight (scroll to + pulse) one of
 * its sections once. Value is the section id (e.g. {@link AUDIO_PERMISSIONS_SECTION}).
 */
export const SETTINGS_HIGHLIGHT_PARAM = 'highlight'

/**
 * Query param carrying the detected meeting source (e.g. "Google Meet") so the
 * audio-permissions banner can explain *why* it's asking. Absent when the
 * capture wasn't meeting-driven (e.g. a manual start).
 */
export const AUDIO_PERMISSIONS_SOURCE_PARAM = 'source'

/**
 * Query param carrying the capture plan main committed to for the blocked
 * capture, so the banner shows exactly that plan's permissions instead of
 * re-deriving one from settings.
 */
export const AUDIO_PERMISSIONS_PLAN_PARAM = 'plan'

const CAPTURE_PLANS = ['dictation', 'electron', 'recall', 'native'] as const satisfies ReadonlyArray<CapturePlan>

/**
 * Query param a deep-link sets to auto-open the "Add API key" dialog for a
 * provider on the Integrations tab, so a caller (e.g. the sidepanel reconnect
 * CTA) can drop the user straight on the key entry instead of making them hunt
 * for the card's overflow menu. Value is the provider id (e.g. `ashby-api-key`).
 * The tab consumes it once, mirroring {@link SETTINGS_HIGHLIGHT_PARAM}.
 */
export const ADD_API_KEY_PARAM = 'addApiKey'

/**
 * Query param the Settings window reads to open the support widget on a new
 * feedback message, consumed once like {@link SETTINGS_HIGHLIGHT_PARAM}. The
 * sidepanel sends feedback here because the widget can't take clicks in the
 * click-through overlay.
 */
export const OPEN_FEEDBACK_PARAM = 'feedback'

export function feedbackSettingsRoute(): string {
  return `/settings/${ACCOUNT_SETTINGS_TAB}?${OPEN_FEEDBACK_PARAM}=1`
}

/**
 * Query param the Subscription tab reads to open the plan comparison rather than
 * the subscription overview. The tab consumes it on Back, mirroring
 * {@link SETTINGS_HIGHLIGHT_PARAM}, so the comparison doesn't reopen itself.
 */
export const SUBSCRIPTION_VIEW_PARAM = 'view'

/** {@link SUBSCRIPTION_VIEW_PARAM} value that opens the plan comparison. */
export const SUBSCRIPTION_PLANS_VIEW = 'plans'

/**
 * Query param the Subscription tab reads to open a top-up's purchase on arrival,
 * consumed once like {@link SETTINGS_HIGHLIGHT_PARAM}. Value is the top-up kind.
 */
export const SUBSCRIPTION_TOP_UP_PARAM = 'topUp'

/**
 * Total budget to grant the audio-capture permissions before we abort a meeting
 * recording that's waiting on them. Shared by the meeting permission toast (how
 * long it lives) and the main-process capture wait (when it gives up) so the
 * user never loses the recording mid-read on one surface while the other is
 * still counting.
 */
export const AUDIO_PERMISSION_WAIT_TIMEOUT_MS = 5 * 60_000

/** How long the meeting toast waits before also opening Settings as a fallback. */
export const AUDIO_PERMISSION_TOAST_SETTINGS_FALLBACK_MS = 30_000

/**
 * Route (with query params) that opens Settings → Meetings, flashes the
 * audio-permissions banner, and — when the capture was triggered by a detected
 * meeting — passes the source so the banner can explain itself. The caller opens
 * the Settings window with this route (main via WindowManager, renderer via
 * WindowIpc); both navigation paths preserve the query string.
 */
export function audioPermissionsSettingsRoute(meetingSource?: string | null, plan?: CapturePlan): string {
  const params = new URLSearchParams({ [SETTINGS_HIGHLIGHT_PARAM]: AUDIO_PERMISSIONS_SECTION })
  if (meetingSource) {
    params.set(AUDIO_PERMISSIONS_SOURCE_PARAM, meetingSource)
  }
  if (plan) {
    params.set(AUDIO_PERMISSIONS_PLAN_PARAM, plan)
  }
  return `/settings/${MEETINGS_SETTINGS_TAB}?${params.toString()}`
}

export function capturePlanFromSearchParams(params: URLSearchParams): CapturePlan | undefined {
  const plan = params.get(AUDIO_PERMISSIONS_PLAN_PARAM)
  return CAPTURE_PLANS.find((known) => known === plan)
}

/**
 * Route that opens Settings → Integrations and auto-opens the "Add API key"
 * dialog for `providerId` (e.g. `ashby-api-key`). Used by the sidepanel
 * reconnect CTA for API-key providers, which have no OAuth flow to redirect to.
 * Both navigation paths preserve the query string.
 */
export function integrationsAddApiKeyRoute(providerId: string): string {
  const params = new URLSearchParams({ [ADD_API_KEY_PARAM]: providerId })
  return `/settings/${INTEGRATIONS_SETTINGS_TAB}?${params.toString()}`
}

/** Route that opens Settings → Subscription, where usage and top-ups live. */
export function subscriptionRoute(): string {
  return `/settings/${SUBSCRIPTION_SETTINGS_TAB}`
}

/** Route that opens Settings → Subscription with a top-up's purchase open. */
export function subscriptionTopUpRoute(kind: 'credits' | 'hours'): string {
  const params = new URLSearchParams({ [SUBSCRIPTION_TOP_UP_PARAM]: kind })
  return `/settings/${SUBSCRIPTION_SETTINGS_TAB}?${params.toString()}`
}

/**
 * Route that opens Settings → Subscription on the plan comparison. The Manage
 * Subscription modal sends users here because the comparison needs the full pane
 * width. Without the param this lands on the tab the caller may already be
 * looking at, which reads as the button doing nothing.
 */
export function subscriptionPlansRoute(): string {
  const params = new URLSearchParams({ [SUBSCRIPTION_VIEW_PARAM]: SUBSCRIPTION_PLANS_VIEW })
  return `/settings/${SUBSCRIPTION_SETTINGS_TAB}?${params.toString()}`
}
